import React, { forwardRef, useCallback, useMemo, useState, useRef, useEffect } from 'react';
import _, { flatMap, isEmpty, uniqBy, find, isArray } from 'lodash';
import { FieldTypes, urlRegexValidator, parseSelectOptions } from 'utils/formUtils';
import Vocabulary from 'components/Vocabulary';
import { useFeatureFlag } from 'contexts/FeatureFlagContext';
import { Form, Alert } from 'antd';
import GenericMappingForm from '../GenericMappingForm';
import ToolsIntegrationsAPI, { ToolFindingStatusMappingSupportAPI } from "restAPI/ToolsIntegrationAPI";
const STATUS_OPTIONS = ["new", "pending-program-review", "triaged", "needs-more-info", "resolved", "not-applicable", "informative", "duplicate", "spam", "retesting"];


const HackerOneMappingForm = forwardRef((props, ref) => {
  const STATUS = useMemo(() => ["open", "confirmed",  "acceptrisk", "falsepositive", "mitigated", "suppressed", "triage", "inprogress", "controlled"], []);
  const [organisation, setOrganisation] = useState(props.record ? props.record.orgId : undefined)
  const [configType, setConfigType ] = useState(props.record ? props.record.configType : undefined);
  const [statusMappingEnabled, setStatusMappingEnabled] = useState(false)
  const featureFlags = useFeatureFlag();
  const [form] = Form.useForm();
  const [mappedProjectList] = useState(() => {
    if (props.record) {
      const { orgId, orgName, applicationId, applicationName, inboxId, inboxName  } = props.record;
      const idArray = applicationId ? applicationId.split(',') : inboxId.split(',');
      const nameArray = applicationId ? applicationName.split(','): inboxName.split(',');
       
      return {
         org: [{ id: orgId, name: orgName }],
         program: applicationId ? idArray.map((id, index) => ({id, name: nameArray[index]})) : [],
         inbox: inboxId ? idArray.map((id, index) => ({id, name: nameArray[index]})): []
        };
    }
    return { org: [], program: [], inbox: [] };
  });

  const projectMapRef = useRef({ org: mappedProjectList.org });

  const [initialValues] = useState(() => {
    if (props.record) {
      return [];
    }
    return [
      {
         ..._.cloneDeep(DEFAULT_STATUS_MAPPING),
         product: props.defaultProduct,
         subProduct: props.defaultSubProduct,
         subProductId: props.defaultSubProductId,
         environment: props.defaultEnvironment, 
      }];
  });
  const [values, setValues] = useState(initialValues);

  useEffect(() =>{
    const { promise, cancel } = ToolFindingStatusMappingSupportAPI.getToolMappingStatus(_.toLower(props.toolName));
    promise.then(res => setStatusMappingEnabled(res.statusMappingToggle))
    return cancel;
  },[props.toolName])

  const fetchResponse = useCallback((params) => {
    const type =  params?.type ? params.type : 'org';
    const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, { login_id: props.loginConfigId, ...params });
    promise
      .then((response) => {
        let { projects } = response;
        if (mappedProjectList[type].length > 0 && !isEmpty(projects)) {
          response.projects = uniqBy(
            [...projects, ...mappedProjectList[type]],
            "id"
          );
        }
        
        projectMapRef.current[type] = response.projects;
      })
      .catch((err) => {
        console.error(`Problem fetching ${type}`);
      });

    return { promise, cancel };
  }, [props.loginConfigId, props.toolName, mappedProjectList]);

  const getExtraData = useCallback((config) => {
    let data = {};
      let { orgId, configType, projectId } = config;
      projectId = isArray(projectId) ? projectId : [projectId];
      const orgName = find(projectMapRef.current.org, (obj) => obj.id === orgId)?.name;

      const mappedData = configType ? projectMapRef.current[configType].reduce((acc,cv) =>{
        const { id, name } = cv;
        if(projectId.includes(id)){
          acc.id.push(id);
          acc.name.push(name);
        }
        return acc;
      },{id:[],name:[]}) : undefined;
      
      const projectData = configType ? configType === 'inbox' ? { inboxId: mappedData.id.join(','), inboxName: mappedData.name.join(',') } : { applicationId: mappedData.id.join(','), applicationName: mappedData.name.join(',') } : {};
      data = {
        orgId, orgName, configType,
        ...projectData
      }
    

    const { url } = config;
    const checkEmpty = (status) => isEmpty(status) ? [] : status;
    const jsonData = [{"url": url}, {status: STATUS.reduce((acc, status) => {
      acc[status] = checkEmpty(config[status]);
      return acc
    }, {})}];
      return ({
        customFieldMapping: JSON.stringify(jsonData),
        ...data
      })
  }, [STATUS]);

  const parseExtraFieldsData = useCallback((config) => {
    const { configType, inboxId, applicationId } = config;
    const customMappingJson = JSON.parse(config.customFieldMapping || "[]");
    const status = customMappingJson[1]?.status;
    let pareseData = {}
    if(config.customFieldMapping){
      pareseData = {
        ...pareseData,
        url: customMappingJson[0]?.url,
        ...status
      }
    }
    if(configType) {
      pareseData = {
        ...pareseData,
        projectId: inboxId ? inboxId.split(',') : applicationId  ? applicationId.split(',') : undefined
      }
    }
    return pareseData;
  }, []);

  const onValuesChange = useCallback((value) => {
    _.find(STATUS, status => {
      if (value.configurations[0]?.[status]) {
        const values = form.getFieldsValue().configurations;
        setValues(values);
        return true;
      }
    })
  }, [form, STATUS]);

  const getFormOptions = useCallback((fieldKey) => {

    let selectedValues = values?.[0];

    // fetch selected data from the form while edit in customFieldMapping
    if (!selectedValues && props.record?.customFieldMapping) {
      selectedValues = parseExtraFieldsData(props.record);
    }
    let formData = _.omit(selectedValues, fieldKey);
    let selectedKeys = flatMap(formData).filter(key => key !== undefined);
    return _.map(STATUS_OPTIONS, option => ({
      label: option, 
      value: option, 
      disabled: selectedKeys.indexOf(option) !== -1
    }))
  }, [values, props.record, parseExtraFieldsData]);

  const handleOnChange = useCallback((value, field, type = 'org') => {
    const { name } = field;
    const configurations = form.getFieldValue("configurations");
    if(type === 'config') {
      setConfigType(value);
      configurations[name] = {
        ...configurations[name],
        projectId: undefined
      }
    } else {
      setOrganisation(value);
      configurations[name] = {
        ...configurations[name],
        projectId: undefined,
        configType: undefined
      }
    }
    
    form.setFieldsValue({ configurations });
  }, [form]);

  
  const extraField = useMemo(() => [
   {
        name: "orgId",
        label: "HackerOne Organization",
        type: FieldTypes.SELECT,
        colSpan: 12,
        props: {
            autoFocus: true,
            onChange: handleOnChange,
            api: fetchResponse,
            parseSelectOptions: (res) => parseSelectOptions(res.projects)
        },
        required: true
      },
      {
        colSpan: 12,
        type: FieldTypes.LABEL
      },
      {
        name: "configType",
        label: "HackerOne Config",
        required: true,
        type: FieldTypes.SELECT,
        colSpan: 8,
        options: [{label: 'Project', value: 'program'}, {label: 'Inbox', value: 'inbox'}],
        props: {
          onChange: (value, field) => handleOnChange(value, field, 'config')
        }
      },
      {
        name: "projectId",
        label: "HackerOne Projects/Inbox",
        required: true,
        type: featureFlags.HACKERONE_MANY_TO_ONE ? FieldTypes.MULTI_SELECT : FieldTypes.SELECT,
        colSpan: 8,
        props: {
          api: organisation && configType && fetchResponse,
          apiParams: { organisation, type: configType},
          disabled: isEmpty(organisation) || isEmpty(configType),
          parseSelectOptions: (res) => parseSelectOptions(res.projects)
        }
      },
    {
    name: 'url',
    colSpan: 8,
    type: FieldTypes.ANTD_DEFAULT_INPUT,
    label: 'HackerOne Custom Field for URL',
    rules: [urlRegexValidator]
  }, 
  {
    name: "open",
    label: <Vocabulary name = "open"/>,
    colSpan: 4,
    type: FieldTypes.MULTI_SELECT,
    options: getFormOptions("open"),
    props: {
      allowClear: true
    }
  },
  {
    name: "confirmed",
    colSpan: 4,
    label: <Vocabulary name = "confirmed"/>,
    type: FieldTypes.MULTI_SELECT,
    options: getFormOptions("confirmed"),
    props: {
      allowClear: true
    }
  },
  {
    name: "acceptrisk",
    colSpan: 4,
    label:  <Vocabulary name = "acceptRisk"/>,
    type: FieldTypes.MULTI_SELECT,
    options: getFormOptions("acceptrisk"),
    props: {
      allowClear: true
    }
  },
  {
    name: "falsepositive",
    colSpan: 4,
    label: <Vocabulary name = "falsePositive"/>,
    type: FieldTypes.MULTI_SELECT,
    options: getFormOptions("falsepositive"),
    props: {
      allowClear: true
    }
  },
  {
    name: "mitigated",
    colSpan: 4,
    label: <Vocabulary name = "mitigated"/>,
    type: FieldTypes.MULTI_SELECT,
    options: getFormOptions("mitigated"),
    props: {
      allowClear: true
    }
  },
  {
    name: "suppressed",
    colSpan: 4,
    label: <Vocabulary name = "suppressed"/>,
    type: FieldTypes.MULTI_SELECT,
    options: getFormOptions("suppressed"),
    props: {
      allowClear: true
    }
  },
  {
    name: "triage",
    colSpan: 4,
    label: <Vocabulary name = "triageStatus"/>,
    type: FieldTypes.MULTI_SELECT,
    options: getFormOptions("triage"),
    props: {
      allowClear: true
    }
    },
    {
      name: "inprogress",
      colSpan: 4,
      label: <Vocabulary name = "inProgressStatus"/>,
      type: FieldTypes.MULTI_SELECT,
      options: getFormOptions("inprogress"),
      props: {
        allowClear: true
      }
    },
    {
      name: "controlled",
      colSpan: 4,
      label: <Vocabulary name = "controlledStatus"/>,
      type: FieldTypes.MULTI_SELECT,
      options: getFormOptions("controlled"),
      props: {
        allowClear: true
      }
    },
    ...(statusMappingEnabled ? 
    [{
        name: "message",
        type: FieldTypes.LABEL,
        value: <Alert type='warning' message={<>Only Findings of mapped {props.toolName} status will be ingested.</>}/>
    }]: [])
    
], [getFormOptions, organisation, configType, featureFlags, fetchResponse, handleOnChange, statusMappingEnabled, props.toolName]);

  return (
    <GenericMappingForm
      ref={ref}
      form={form}
      getExtraFieldsData={getExtraData}
      parseExtraFieldsDataFromResponse={parseExtraFieldsData}
      onValuesChange={onValuesChange}
      extraFields={extraField}
      formDefaultValues={initialValues}
      newRowDefaultValue={initialValues[0]}
      {...props}
    />
  );
});

 

export default HackerOneMappingForm;

const DEFAULT_STATUS_MAPPING = {
  confirmed: ["triaged"],
  mitigated: ["resolved"],
  open: ["new", "pending-program-review", "needs-more-info", "retesting", "informative", "duplicate"],
  falsepositive: ["not-applicable", "spam"],
};